import {useEffect, useState} from "react";
import {ReadOnlyDashboardConfiguration} from "@/types";
import {api} from "@/lib/api.tsx";
import {useHub} from "../Hub";
import {
    Badge,
    Group,
    Pagination,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip
} from "@mantine/core";
import {
    IconArrowsSort,
    IconSortAscendingLetters,
    IconSortDescendingLetters,
    IconUsersGroup
} from "@tabler/icons-react";

const ReadOnlyAdminDashboard = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [sortTerm, setSortTerm] = useState('name asc');
    const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('active');
    const [counties, setCounties] = useState<ReadOnlyDashboardConfiguration[]>([]);
    const [visibleCounties, setVisibleCounties] = useState<ReadOnlyDashboardConfiguration[]>([]);
    const [filteredCounties, setFilteredCounties] = useState<ReadOnlyDashboardConfiguration[]>([]);
    const {dashboardUpdatedAt} = useHub();

    useEffect(() => {
        fetchCounties();
    }, []);

    // Silently re-fetch when the backend snapshot job pings the hub (initial mount fetch above is unchanged)
    useEffect(() => {
        if (dashboardUpdatedAt) {
            fetchCounties();
        }
    }, [dashboardUpdatedAt]);

    useEffect(() => {
        let newCounties = [...counties]
        if (searchTerm) {
            newCounties = [...newCounties.filter((county) => {
                return county?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) || county?.code?.toLowerCase().includes(searchTerm?.toLowerCase());
            })];
        }
        if (statusFilter !== 'all') {
            const enabled = statusFilter === 'active';
            newCounties = newCounties.filter((county) => county.isGuardDogEnabled === enabled);
        }
        setFilteredCounties(newCounties);
        setVisibleCounties([...newCounties.sort((a, b) => {
            if (sortTerm.includes('name')) {
                return sortTerm.includes('desc') ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
            } else if (sortTerm.includes('code')) {
                return sortTerm.includes('desc') ? b.code.localeCompare(a.code) : a.code.localeCompare(b.code);
            }
            return 0;
        }).slice(page * pageSize, (page + 1) * pageSize)]);
    }, [page, pageSize, sortTerm, searchTerm, statusFilter, counties]);
    
    const fetchCounties = async () => {
        try {
            const response: ReadOnlyDashboardConfiguration[] = await api.get(`/admin/read-only-dashboard`);
            setCounties(response);
        } catch (error) {
            console.error('Error fetching counties:', error);
        }
    }

    return (
        <>
            <Paper withBorder p='md' radius={'md'}>
                <Group justify='space-between'>
                    <Title order={4}>Counties</Title>
                    <IconUsersGroup size={22} stroke={1.5} />
                </Group>
                <Stack>
                    <Group justify='flex-end' mt='md'>
                        <TextInput
                            placeholder="Search by name or code"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        />
                        <Select
                            value={statusFilter}
                            onChange={(value) => {
                                setStatusFilter((value as 'active' | 'inactive' | 'all') ?? 'active');
                                setPage(0); // reset to page 1 on filter change
                            }}
                            data={[
                                { value: 'active', label: 'Active Counties' },
                                { value: 'inactive', label: 'Inactive Counties' },
                                { value: 'all', label: 'All Counties' }
                            ]}
                        />
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCounties.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                    <Table.ScrollContainer minWidth={2150}>
                        <Table layout="fixed">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th w={150}><Group tabIndex={0} role="button" onClick={() => {
                                        const newSortTerm = sortTerm.includes('Name') ? (sortTerm.includes('desc') ? 'name asc' : 'name desc') : 'name asc';
                                        setSortTerm(newSortTerm);
                                    }}><Text>Name</Text>{sortTerm.includes('name') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                    <Table.Th w={70}><Group tabIndex={0} role="button" onClick={() => {
                                        const newSortTerm = sortTerm.includes('code') ? (sortTerm.includes('desc') ? 'code asc' : 'code desc') : 'code asc';
                                        setSortTerm(newSortTerm);
                                    }}><Text>Code</Text>{sortTerm.includes('code') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                    <Table.Th w={90}><Group><Text>Enabled</Text></Group></Table.Th>
                                    <Table.Th w={90}><Group><Text>Use Mailer</Text></Group></Table.Th>
                                    <Table.Th w={110}><Group><Text>Has Link URL</Text></Group></Table.Th>
                                    <Table.Th w={120}><Group><Text>Has Email Template</Text></Group></Table.Th>
                                    <Table.Th w={120}><Group><Text>Mailer Doc Types</Text></Group></Table.Th>
                                    <Table.Th w={85}><Group><Text>One Page</Text></Group></Table.Th>
                                    <Table.Th w={95}><Group><Text>Add Sample</Text></Group></Table.Th>
                                    <Table.Th w={100}><Group><Text>Watermark</Text></Group></Table.Th>
                                    <Table.Th w={95}><Group><Text>Grayscale</Text></Group></Table.Th>
                                    <Table.Th w={120}><Group><Text>Has Template Path</Text></Group></Table.Th>
                                    <Table.Th w={110}><Group><Text>Delivery Enabled</Text></Group></Table.Th>
                                    <Table.Th w={210}><Group><Text>Last File Received</Text></Group></Table.Th>
                                    <Table.Th w={210}><Group><Text>Last File Processed</Text></Group></Table.Th>
                                    <Table.Th w={210}><Group><Text>Last File For Mailer</Text></Group></Table.Th>
                                    <Table.Th w={130}><Group><Text>Last Doc Received</Text></Group></Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {visibleCounties.map((county) => (
                                    <Table.Tr key={county.id}>
                                        <Table.Td>{county.name}</Table.Td>
                                        <Table.Td>{county.code}</Table.Td>
                                        <Table.Td>
                                            <Badge color={county.isGuardDogEnabled ? 'green' : 'pq-red'}>
                                                {county.isGuardDogEnabled ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.createCountyMailer ? 'green' : 'pq-red'}>
                                                {county.createCountyMailer ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Tooltip label={county.linkUrl} disabled={!county.hasLinkUrl}>
                                                <Badge color={county.hasLinkUrl ? 'green' : 'pq-red'}>
                                                    {county.hasLinkUrl ? 'Yes' : 'No'}
                                                </Badge>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            <Tooltip label={county.alertSendGridEmailTemplateId} disabled={!county.hasAlertSendGridEmailTemplateId}>
                                                <Badge color={county.hasAlertSendGridEmailTemplateId ? 'green' : 'pq-red'}>
                                                    {county.hasAlertSendGridEmailTemplateId ? 'Yes' : 'No'}
                                                </Badge>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            <Tooltip label={county.mailerDocumentTypes?.join(', ')} disabled={!county.hasMailerDocumentTypes}>
                                                <Badge color={county.hasMailerDocumentTypes ? 'green' : 'pq-red'}>
                                                    {county.hasMailerDocumentTypes ? 'Yes' : 'No'}
                                                </Badge>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.useOnePageMailer ? 'green' : 'pq-red'}>
                                                {county.useOnePageMailer ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.addSampleToMailer ? 'green' : 'pq-red'}>
                                                {county.addSampleToMailer ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.useWatermark ? 'green' : 'pq-red'}>
                                                {county.useWatermark ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.grayscaleMailer ? 'green' : 'pq-red'}>
                                                {county.grayscaleMailer ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                            <Tooltip label={county.mailerTemplatePath} disabled={!county.hasMailerTemplatePath}>
                                                <Badge color={county.hasMailerTemplatePath ? 'green' : 'pq-red'}>
                                                    {county.hasMailerTemplatePath ? 'Yes' : 'No'}
                                                </Badge>
                                            </Tooltip>
                                        </Table.Td>
                                        <Table.Td>
                                            <Badge color={county.isDeliveryEnabled ? 'green' : 'pq-red'}>
                                                {county.isDeliveryEnabled ? 'Yes' : 'No'}
                                            </Badge>
                                        </Table.Td>
                                        <Table.Td>{county.lastFileReceived || 'Not set'}</Table.Td>
                                        <Table.Td>{county.lastFileProcessed || 'Not set'}</Table.Td>
                                        <Table.Td>{county.lastFileCreatedForCountyMailer || 'Not set'}</Table.Td>
                                        <Table.Td>{county.lastDocumentReceived || 'Not set'}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <Group justify='flex-end' mt='md'>
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCounties.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                </Stack>
            </Paper>
        </>
    );
}

export default ReadOnlyAdminDashboard;